import { useEffect, useRef, useState } from 'react'

/* Styles */
import style from './AdminSurveyReport.module.css'
import 'react-quill-new/dist/quill.snow.css';

/* Libraries */
import { useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import ReactQuill from 'react-quill-new';

/* Apis */
import { adminGetReportApi, adminSaveReportApi } from '../../../api/admin/survey'
import { adminGetUserNameApi } from '../../../api/admin/user'

/* Components */
import ReportSpecChart from '../../../components/survey/report/ReportSpecChart'
import AdminInputFileList from '../../../components/admin/common/AdminInputFileList'
import AdminSavedFileList from '../../../components/admin/common/AdminSavedFileList'

/* Utils */
import { swalAlert, swalErrorAlert } from '../../../utils/swal/swal'
import { reactQuillFormats, reactQuillModules } from '../../../utils/editor/reactQuill'


const AdminSurveyReportWrite = () => {
  const { userId } = useParams()

  /* React Hook Form */
  const { register, handleSubmit, reset, watch, control } = useForm()


  /* 보고서 정보 */
  const [userName, setUserName] = useState('')
  const [reportInfo, setReportInfo] = useState(null)
  const [savedFiles, setSavedFiles] = useState([]) // 기존 파일
  const [updatedDate, setUpdatedDate] = useState('')

  useEffect(() => {
    getUserName(userId)
    getReportInfo(userId)

  }, [userId])

  useEffect(() => {
    reset(reportInfo)
  }, [reportInfo])

  const getUserName = async (userId) => {
    if (!userId) {
      setUserName('')
      return
    }

    try {
      const response = await adminGetUserNameApi(userId)

      if (response.data?.code === 200) {
        const userName = response.data?.resData?.user_name
        setUserName(userName)
      } else {
        setUserName('')
      }

    } catch (error) {
      swalErrorAlert()
      return
    }
  }

  const getReportInfo = async (userId) => {
    try {
      const response = await adminGetReportApi(userId)

      if (response.status === 200) {
        const jsonData = response.data?.resData?.json_data ? response.data?.resData?.json_data : null
        const savedFiles = response.data?.resData?.files ? response.data?.resData?.files : []
        const updatedData = response.data?.resData?.updated_date ? response.data?.resData?.updated_date : ''

        setReportInfo(jsonData)
        setSavedFiles(savedFiles)
        setUpdatedDate(updatedData)
        return
      }
      else if (response.status === 204) {
        setReportInfo(null)
        setSavedFiles([])
        setUpdatedDate('')
        return
      }

    } catch (error) {
      swalErrorAlert()
      return
    }
  }


  /* 스펙분석 - 차트데이터 */
  const [specChartData, setSpecChartData] = useState([0, 0, 0, 0, 0])
  const specGpaValue = watch("specGpa");
  const specAccreditedValue = watch("specAccredited");
  const specClubValue = watch("specClub");
  const specAwardValue = watch("specAward");
  const specLeadershipValue = watch("specLeadership");
  useEffect(() => {
    const updatedData = [
      Number(specGpaValue) || 0,
      Number(specAccreditedValue) || 0,
      Number(specClubValue) || 0,
      Number(specAwardValue) || 0,
      Number(specLeadershipValue) || 0,
    ];

    setSpecChartData(updatedData);
  }, [specGpaValue, specAccreditedValue, specClubValue, specAwardValue, specLeadershipValue]);


  /* 심리검사 결과분석 - 파일데이터 */
  // 다운로드 파일
  const [deleteSavedFile, setDeleteSavedFile] = useState([]) // 기존 파일 삭제
  const onClickDeleteSavedFile = (deleteItemPath) => {
    setSavedFiles((prevState) => { return prevState.filter((item) => item !== deleteItemPath) })
    setDeleteSavedFile((state) => [...state, deleteItemPath])
  }

  // 새로업로드된파일
  const [inputFiles, setInputFiles] = useState([])
  const fileInputRef = useRef(null);
  const handleFileInputChange = (event) => {
    const inputFiles = Array.from(event.target.files);
    setInputFiles((prevFiles) => [...prevFiles, ...inputFiles]);

    event.target.value = "";
  };
  const onClickDeleteFile = (deleteIndex) => {
    setInputFiles((prevFiles) => prevFiles.filter((_, i) => i !== deleteIndex));
  }


  /* Form Submit */
  const onSubmit = async (data) => {
    try {
      // ⚽ Validation

      // Api Data
      const formData = new FormData();

      if (inputFiles && inputFiles.length > 0) {
        inputFiles.forEach((file) => {
          formData.append("fileData", file);
        });
      }

      let jsonData = {}
      Object.keys(data).forEach((key) => {
        if (key !== 'files') jsonData[key] = data[key]
      })

      formData.append("jsonData", JSON.stringify(jsonData))

      formData.append('deleteSavedFile', JSON.stringify(deleteSavedFile))

      const response = await adminSaveReportApi(formData, userId)

      swalAlert(
        "저장 완료",
        response.data.message,
        "success",
        "확인",
        () => { window.location.reload(true); }
      )


    } catch (error) {
      swalErrorAlert()
    } finally {
      setInputFiles([])
      fileInputRef.current.value = ''
    }
  }


  return (
    <div className="container-fluid">
      {/* Heading */}
      <div className="mb-4">
        <h1 className="h3 mb-0">KGS 메가반 학생 분석 보고서 작성</h1>
      </div>

      {/* Content */}
      <div className='card shadow'>
        <div className="card-header py-3">
          <div className='d-flex justify-content-between'>
            <h6 className="m-0 font-weight-bold">{userName} 학생 분석 보고서 작성</h6>
            {updatedDate && <span className='text-primary'>{updatedDate} 에 마지막으로 작성되었습니다.</span>}
          </div>
        </div>


        {/* Form Component */}
        <div className={style.formWrap}>
          <form id='reportForm' className={style.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={style.titleWrap}>
              <h1 className={style.title}>KGS 메가반 학생 분석 보고서</h1>
            </div>

            <div className={style.contentWrap}>

              {/* 1. 학생정보 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>학생정보</h2>

                <div className={style.sectionContent}>
                  <div className='mb-2'>
                    <label htmlFor='userName' className='form-label'>이름</label>
                    <input type="text" {...register('userName')} id="userName" className='form-control' />
                  </div>

                  <div className='mb-2'>
                    <label htmlFor='userGrade' className='form-label'>학년</label>
                    <input type="text" {...register('userGrade')} id="userGrade" className='form-control' />
                  </div>
                </div>

                <div className={style.sectionContent}>
                  <h3 className={style.sectionSubTitle}>대학목표</h3>
                  <div className='mb-2'>
                    <label htmlFor='targetType' className='form-label'>전형</label>
                    <input type="text" {...register('targetType')} id="targetType" className='form-control' />
                  </div>

                  <div className='mb-2'>
                    <label htmlFor='targetUniversity' className='form-label'>목표대학</label>
                    <input type="text" {...register('targetUniversity')} id="targetUniversity" className='form-control' />
                  </div>

                  <div className='mb-2'>
                    <label htmlFor='targetMajor' className='form-label'>전공</label>
                    <input type="text" {...register('targetMajor')} id="targetMajor" className='form-control' />
                  </div>
                </div>
              </div>
              <hr />


              {/* 2. 객관적인 스펙 분석 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>객관적인 스펙 분석</h2>

                <div className={style.sectionContent}>
                  <div className={style.specChartWrap}>
                    <ReportSpecChart chartData={specChartData} />
                  </div>

                  <div className={style.specRadioWrap}>
                    <div className={style.specRadioItem}>
                      <div className={style.specName}>GPA</div>
                      <ul className={style.specRadioList}>
                        <li>
                          <input type="radio" {...register('specGpa')} id="specGpa3" value={3} className='form-check-input' />
                          <label htmlFor='specGpa3' className='form-check-label ps-2'>상</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specGpa')} id="specGpa2" value={2} className='form-check-input' />
                          <label htmlFor='specGpa2' className='form-check-label ps-2'>중</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specGpa')} id="specGpa1" value={1} className='form-check-input' />
                          <label htmlFor='specGpa1' className='form-check-label ps-2'>하</label>
                        </li>
                      </ul>
                    </div>

                    <div className={style.specRadioItem}>
                      <div className={style.specName}>공인성적</div>
                      <ul className={style.specRadioList}>
                        <li>
                          <input type="radio" {...register('specAccredited')} id="specAccredited3" value={3} className='form-check-input' />
                          <label htmlFor='specAccredited3' className='form-check-label ps-2'>상</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specAccredited')} id="specAccredited2" value={2} className='form-check-input' />
                          <label htmlFor='specAccredited2' className='form-check-label ps-2'>중</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specAccredited')} id="specAccredited1" value={1} className='form-check-input' />
                          <label htmlFor='specAccredited1' className='form-check-label ps-2'>하</label>
                        </li>
                      </ul>
                    </div>

                    <div className={style.specRadioItem}>
                      <div className={style.specName}>동아리활동</div>
                      <ul className={style.specRadioList}>
                        <li>
                          <input type="radio" {...register('specClub')} id="specClub3" value={3} className='form-check-input' />
                          <label htmlFor='specClub3' className='form-check-label ps-2'>상</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specClub')} id="specClub2" value={2} className='form-check-input' />
                          <label htmlFor='specClub2' className='form-check-label ps-2'>중</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specClub')} id="specClub1" value={1} className='form-check-input' />
                          <label htmlFor='specClub1' className='form-check-label ps-2'>하</label>
                        </li>
                      </ul>
                    </div>

                    <div className={style.specRadioItem}>
                      <div className={style.specName}>수상실적</div>
                      <ul className={style.specRadioList}>
                        <li>
                          <input type="radio" {...register('specAward')} id="specAward3" value={3} className='form-check-input' />
                          <label htmlFor='specAward3' className='form-check-label ps-2'>상</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specAward')} id="specAward2" value={2} className='form-check-input' />
                          <label htmlFor='specAward2' className='form-check-label ps-2'>중</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specAward')} id="specAward1" value={1} className='form-check-input' />
                          <label htmlFor='specAward1' className='form-check-label ps-2'>하</label>
                        </li>
                      </ul>
                    </div>

                    <div className={style.specRadioItem}>
                      <div className={style.specName}>리더십활동</div>
                      <ul className={style.specRadioList}>
                        <li>
                          <input type="radio" {...register('specLeadership')} id="specLeadership3" value={3} className='form-check-input' />
                          <label htmlFor='specLeadership3' className='form-check-label ps-2'>상</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specLeadership')} id="specLeadership2" value={2} className='form-check-input' />
                          <label htmlFor='specLeadership2' className='form-check-label ps-2'>중</label>
                        </li>
                        <li>
                          <input type="radio" {...register('specLeadership')} id="specLeadership1" value={1} className='form-check-input' />
                          <label htmlFor='specLeadership1' className='form-check-label ps-2'>하</label>
                        </li>
                      </ul>
                    </div>

                  </div>
                </div>
              </div>
              <hr />


              {/* 3. 심리검사 결과 분석 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>심리검사 결과 분석</h2>

                <div className={style.sectionContent}>
                  <div className='mb-5'>
                    <h3 className={style.sectionSubTitle}>결과 분석지 다운로드</h3>
                    <div style={{ padding: "1rem", border: "1px solid #e5e5e5" }}>
                      <AdminSavedFileList savedFiles={savedFiles} onClickDeleteSavedFile={onClickDeleteSavedFile} fileFolderName={'report'} isEdit={true} />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <h3 className={style.sectionSubTitle}>결과 분석지 업로드</h3>
                    <div style={{ padding: "1rem", border: "1px solid #e5e5e5" }}>
                      <input type="file" ref={fileInputRef} id='files' className="d-none" onChange={handleFileInputChange} multiple />
                      <label htmlFor='files' className='btn btn-secondary'>파일선택</label>
                      <AdminInputFileList inputFiles={inputFiles} onClickDeleteFile={onClickDeleteFile} />
                    </div>
                  </div>

                  {/* MCI */}
                  <div className='mt-5'>
                    <h3 className={style.sectionSubTitle}>MCI 검사 분석</h3>
                    <div>
                      <div className='mb-3'>
                        <label htmlFor='mciJob' className='form-label'>1. 추천직업</label>
                        <textarea {...register('mciJob')} id="mciJob" className='form-control' rows={2} />
                      </div>

                      <div className='mb-3'>
                        <label htmlFor='mciDepartment' className='form-label'>2. 추천계열</label>
                        <textarea {...register('mciDepartment')} id="mciDepartment" className='form-control' rows={2} />
                      </div>

                      <div className='mb-3'>
                        <label htmlFor='mciMajor' className='form-label'>3. 추천전공</label>
                        <textarea {...register('mciMajor')} id="mciMajor" className='form-control' rows={2} />
                      </div>
                    </div>
                  </div>

                  {/* SLT */}
                  <div className='mt-5'>
                    <h3 className={style.sectionSubTitle}>SLT 검사 분석</h3>
                    <div>
                      <div className='mb-3'>
                        <label htmlFor='sltStrength' className='form-label'>1. 강점 학습전략</label>
                        <textarea {...register('sltStrength')} id="sltStrength" className='form-control' rows={4} />
                      </div>

                      <div className='mb-3'>
                        <label htmlFor='sltWeakness' className='form-label'>2. 약점 학습전략</label>
                        <textarea {...register('sltWeakness')} id="sltWeakness" className='form-control' rows={4} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr />

              {/* 4. SWOT 분석 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>SWOT 분석</h2>

                <div className={style.sectionContent}>
                  <div className='mt-5'>
                    <div className='mb-3'>
                      <label htmlFor='swotStrength' className='form-label'>1. 강점(Strength)</label>
                      <textarea {...register('swotStrength')} id="swotStrength" className='form-control' rows={4} />
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='swotWeakness' className='form-label'>2. 약점(Weakness)</label>
                      <textarea {...register('swotWeakness')} id="swotWeakness" className='form-control' rows={4} />
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='swotOpportunity' className='form-label'>3. 기회(Opportunity)</label>
                      <textarea {...register('swotOpportunity')} id="swotOpportunity" className='form-control' rows={4} />
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='swotThreat' className='form-label'>4. 위협(Threat)</label>
                      <textarea {...register('swotThreat')} id="swotThreat" className='form-control' rows={4} />
                    </div>

                    <div className='mb-3'>
                      <label htmlFor='swotKeyword' className='form-label'>목표대학 합격을 위한 중심 키워드</label>
                      <textarea {...register('swotKeyword')} id="swotKeyword" className='form-control' rows={2} />
                    </div>
                  </div>
                </div>
              </div>
              <hr />


              {/* 5. 추천 전형, 대학, 전공 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>추천 전형, 대학, 전공</h2>

                <div className={style.sectionContent}>
                  <div className='mb-3'>
                    <label htmlFor='recommendType' className='form-label'>1. 추천전형</label>
                    <input type="text" {...register('recommendType')} id="recommendType" className='form-control' />
                  </div>

                  <div className='mb-3'>
                    <label htmlFor='recommendUniversity' className='form-label'>2. 추천대학</label>
                    <textarea {...register('recommendUniversity')} id="recommendUniversity" className='form-control' rows={6} />
                  </div>

                  <div className='mb-3'>
                    <label htmlFor='recommendMajor' className='form-label'>3. 추천전공</label>
                    <textarea {...register('recommendMajor')} id="recommendMajor" className='form-control' rows={6} />
                  </div>
                </div>
              </div>
              <hr />


              {/* 6. 단기 학습 목표 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>단기 학습 목표</h2>

                {/* <div className={style.sectionContent}>
                  <h3 className={style.sectionSubTitle}>학기말까지 목표</h3>
                  <div className=''>
                    <div className='mb-3'>
                      <label htmlFor='shortSemesterSchoolScore' className='form-label'>1. 학교성적</label>
                      <input type="text" {...register('shortSemesterSchoolScore')} id="shortSemesterSchoolScore" className='form-control mb-2' placeholder='목표점수' />
                      <textarea {...register('shortSemesterSchoolComment')} className='form-control' placeholder='목표점수를 위한 코멘트를 입력해 주세요.' rows={4} />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='shortSemesterOfficial' className='form-label'>2. 공인시험성적</label>
                      <textarea {...register('shortSemesterOfficial')} className='form-control' placeholder='시험 이름, 목표점수, 목표점수를 위한 코멘트를 입력해 주세요.' rows={6} />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='shortSemesterExtra' className='form-label'>3. 비교과활동</label>
                      <textarea {...register('shortSemesterExtra')} className='form-control' placeholder='수상, 전공관련 탐색활동, 기타 에 대한 내용을 입력해 주세요.' rows={6} />
                    </div>
                  </div>
                </div> */}

                <div className={style.sectionContent}>
                  <h3 className={style.sectionSubTitle}>학년말까지 목표</h3>
                  <div className=''>
                    <div className='mb-3'>
                      <label htmlFor='shortYearSchoolScore' className='form-label'>1. 학교성적</label>
                      <input type="text" {...register('shortYearSchoolScore')} id="shortYearSchoolScore" className='form-control mb-2' placeholder='목표점수' />
                      <textarea {...register('shortYearSchoolComment')} className='form-control' placeholder='목표점수를 위한 코멘트를 입력해 주세요.' rows={4} />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='shortYearOfficial' className='form-label'>2. 공인시험성적</label>
                      <textarea {...register('shortYearOfficial')} className='form-control' placeholder='시험 이름, 목표점수, 목표점수를 위한 코멘트를 입력해 주세요.' rows={6} />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='shortYearExtra' className='form-label'>3. 비교과활동</label>
                      <textarea {...register('shortYearExtra')} className='form-control' placeholder='수상, 전공관련 탐색활동, 기타 에 대한 내용을 입력해 주세요.' rows={6} />
                    </div>
                  </div>
                </div>

              </div>
              <hr />


              {/* 7. 장기 학습 목표 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>장기 학습 목표</h2>

                <div className={style.sectionContent}>
                  <h3 className={style.sectionSubTitle}>12학년 1학기까지 목표</h3>
                  <div className=''>
                    <div className='mb-3'>
                      <label htmlFor='longSchoolScore' className='form-label'>1. 학교성적</label>
                      <input type="text" {...register('longSchoolScore')} id="longSchoolScore" className='form-control mb-2' placeholder='목표점수' />
                      <textarea {...register('longSchoolComment')} className='form-control' placeholder='목표점수를 위한 코멘트를 입력해 주세요.' rows={4} />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='longOfficial' className='form-label'>2. 공인시험성적</label>
                      <textarea {...register('longOfficial')} className='form-control' placeholder='시험 이름, 목표점수, 목표점수를 위한 코멘트를 입력해 주세요.' rows={6} />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='longExtra' className='form-label'>3. 비교과활동</label>
                      <textarea {...register('longExtra')} className='form-control' placeholder='수상, 전공관련 탐색활동, 기타 에 대한 내용을 입력해 주세요.' rows={6} />
                    </div>
                  </div>
                </div>
              </div>
              <hr />

              {/* 8. 대학 지원 준비 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>대학 지원 준비</h2>

                <div className={style.sectionContent}>
                  <div className='mb-3'>
                    <label htmlFor='readyRecommendation' className='form-label'>1. 지원 대학 추천</label>
                    <textarea {...register('readyRecommendation')} className='form-control' placeholder='지원 대학 추천에 대한 내용을 입력해 주세요.' rows={4} />
                  </div>
                  <div className='mb-3'>
                    <label htmlFor='readyIntroduction' className='form-label'>2. 자기소개서</label>
                    <textarea {...register('readyIntroduction')} className='form-control' placeholder='자기소개서에 대한 내용을 입력해 주세요.' rows={6} />
                  </div>
                  <div className='mb-3'>
                    <label htmlFor='readyDocument' className='form-label'>3. 제출서류</label>
                    <textarea {...register('readyDocument')} className='form-control' placeholder='제출서류에 대한 내용을 입력해 주세요.' rows={6} />
                  </div>
                </div>
              </div>
              <hr />

              {/* 9. 맞춤형 학습 전략 */}
              <div className={style.section}>
                <h2 className={style.sectionTitle}>맞춤형 학습 전략</h2>

                <div className={style.sectionContent}>
                  <div className='mb-3'>
                    <label htmlFor='strategyAcademic' className='form-label'>1. 학업 성취 전략</label>
                    {/* ✏️ Quill Editor */}
                    <Controller
                      name="strategyAcademic"
                      className="ql-editor"
                      control={control}
                      render={({ field }) => (
                        <ReactQuill
                          theme="snow"
                          value={field.value}
                          onChange={field.onChange}
                          modules={reactQuillModules}
                          formats={reactQuillFormats}
                          style={{ height: '200px', marginBottom: '80px' }}
                        />
                      )}
                    />
                  </div>

                  <div className='mb-3'>
                    <label htmlFor='strategyExtracurricular' className='form-label'>2. 비교과 활동 전략</label>
                    <Controller
                      name="strategyExtracurricular"
                      className="ql-editor"
                      control={control}
                      render={({ field }) => (
                        <ReactQuill
                          theme="snow"
                          value={field.value}
                          onChange={field.onChange}
                          modules={reactQuillModules}
                          formats={reactQuillFormats}
                          style={{ height: '200px', marginBottom: '80px' }}
                        />
                      )}
                    />
                  </div>

                  <div className='mb-3'>
                    <label htmlFor='strategyTime' className='form-label'>3. 시간 관리 전략</label>
                    <Controller
                      name="strategyTime"
                      className="ql-editor"
                      control={control}
                      render={({ field }) => (
                        <ReactQuill
                          theme="snow"
                          value={field.value}
                          onChange={field.onChange}
                          modules={reactQuillModules}
                          formats={reactQuillFormats}
                          style={{ height: '200px', marginBottom: '80px' }}
                        />
                      )}
                    />
                  </div>

                  <div className='mb-3'>
                    <label htmlFor='strategyGoal' className='form-label'>4. 목표 관리 전략</label>
                    <Controller
                      name="strategyGoal"
                      className="ql-editor"
                      control={control}
                      render={({ field }) => (
                        <ReactQuill
                          theme="snow"
                          value={field.value}
                          onChange={field.onChange}
                          modules={reactQuillModules}
                          formats={reactQuillFormats}
                          style={{ height: '200px', marginBottom: '80px' }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

            </div>
          </form>

          <div className='mt-5 text-center'>
            <button type='submit' form='reportForm' className='btn btn-primary'>보고서 저장</button>
          </div>
        </div >

      </div >
    </div >
  )
}


export default AdminSurveyReportWrite